
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getTaxPaymentPeriodsAsync } from '../../store/slices/tax/taxThunks'
import AddAdditionalPeriodsModal from '../../components/tax/AddAdditionalPeriodsModal'
import { Button, Grid, Row, Col, Content, Text, FlexboxGrid, Stack } from 'rsuite'
import dayjs from 'dayjs'
import { selectIsGetTaxPaymentPeriodsErrorMsg, selectIsGetTaxPaymentPeriodsInProgress, selectIsGetTaxPaymentPeriodsSuccess, selectPreSelectedTaxPeriod, selectTaxFlowData, selectTaxSteps } from '../../store/slices/tax/taxSelectors'
import IconIndicator from '../../components/user-feedback/IconIndicator'
import Notice from '../../components/user-feedback/Notice'
import TaxReportingStatusIndicator from '../../components/tax/TaxReportingStatusIndicator'
import PortalHeader from '../../components/layout/PortalHeader'
import FlowPageLayout from '../../components/layout/FlowPageLayout'
import { clearPreSelectedTaxPeriod, resetTaxState, updateTaxFlowData } from '../../store/slices/tax/taxSlice'
import { C_TOT_PAYMENT_STATUS_INCOMPLETE } from '../../common/consts'
import { getAddressWithUnitNumber, objectHasProperty, titleize } from '../../common/utils'
import { selectConfigExpressRegistrationTypeVerbiage, selectConfigTaxPaymentPeriod } from '../../store/slices/config/configSelectors'
import { FLOWS_ENUM, TAX_PAYMENT_PERIOD_TYPES_ENUM } from '../../common/enums'
import BasicStyledCheckbox from '../../components/form/BasicStyledCheckbox'
import pluralize from 'pluralize'
import usePlaceNavigate from '../../hooks/usePlaceNavigate'
import { selectUsersRenewableLicenses } from '../../store/slices/dashboard/dashboardSelectors'
import { clearBulkRenewalLicenses, setBulkRenewalLicenses } from '../../store/slices/renew/renewSlice'
import { selectSelectedBulkRenewalLicenses } from '../../store/slices/renew/renewSelectors'

const BulkRenewSelectProperties = () => {
    const dispatch = useDispatch();
    const placeNavigate = usePlaceNavigate();

    const [showNoSelectedLicenseError, setShowNoSelectedLicenseError] = useState(false);
    const [renewableLicenses, setRenewableLicenses] = useState([]);

    const permitType = useSelector(selectConfigExpressRegistrationTypeVerbiage);
    const selectedUserRenewableLicenses = useSelector(selectUsersRenewableLicenses);
    const selectedPreSelectedBulkRenewalLicenses = useSelector(selectSelectedBulkRenewalLicenses);

    useEffect(() => {
        let licenses = selectedUserRenewableLicenses.map(l => ({...l})).map(l => selectedPreSelectedBulkRenewalLicenses.find(item => item.license === l.license) ?? l);
        setRenewableLicenses(licenses ?? []);
    }, [selectedUserRenewableLicenses, selectedPreSelectedBulkRenewalLicenses])

    const toggleSelectAllLicensesForRenewal = (checked) => {
        let updatedLicenses = renewableLicenses.map(rl => ({...rl}));
        updatedLicenses.forEach(l => {;
            l["selected"] = checked
        });
        setRenewableLicenses(updatedLicenses);
    }

    const toggleLicenseForRenewal = (license, checked) => {
        let updatedLicenses = renewableLicenses.map(rl => ({...rl}));
        updatedLicenses.forEach(l => {
            if (l.license === license.license) {
               l["selected"] = checked;
            }
        })
        setRenewableLicenses(updatedLicenses);
    }

    const handleNextClick = () => {
        let valuesToSave = [...renewableLicenses];

        // Check if at least one license has been selected for any year
        const selectedProperties = valuesToSave.filter(license => license.selected === true);

        if (selectedProperties.length > 0) {
            // clear pre-selected payment periods in redux
            dispatch(clearBulkRenewalLicenses());
            // Save data in redux
            dispatch(setBulkRenewalLicenses(selectedProperties));
            return true;
        }
        else {
            setShowNoSelectedLicenseError(true);
            return false;
        }
    }

    const handleBackClick = () => {
        dispatch(clearBulkRenewalLicenses());
        placeNavigate("/dashboard");
    }
    return (
        <FlexboxGrid justify='space-between'>
            <FlexboxGrid.Item colspan={24}>
                <PortalHeader flow={FLOWS_ENUM.BULK_RENEWAL} />
            </FlexboxGrid.Item>

            <FlexboxGrid.Item colspan={24}>
                <Content>
                    <FlowPageLayout
                        title='Select Properties for Renewal'
                        onNext={handleNextClick}
                        onBack={() => { handleBackClick(); }}
                    >
                        <Text className='mb-24'>Your properties that are eligible for renewal are listed below. Please select the properties that you wish to renew at this time.</Text>
                        <div>
                            {
                            renewableLicenses?.length > 0 ?
                                <div className='mb-24' key={`bulk_renewal_table`}>
                                    <Grid fluid className='tax-table'>
                                        <FlexboxGrid className='tax-column-heading-row' align='bottom' as={Row}>
                                            <FlexboxGrid.Item as={Col} xs={24} md={2}>
                                                <div role="grid">
                                                    <FlexboxGrid as={Row} align='bottom' gutter={0}>
                                                        <FlexboxGrid.Item as={Col}>
                                                            <BasicStyledCheckbox 
                                                                onChange={(checked) => {
                                                                    toggleSelectAllLicensesForRenewal(checked)
                                                                }} 
                                                            />
                                                        </FlexboxGrid.Item>
                                                        <FlexboxGrid.Item as={Col} mdHidden lgHidden xlHidden xxlHidden>Select/Unselect all</FlexboxGrid.Item>
                                                    </FlexboxGrid>
                                                </div>
                                            </FlexboxGrid.Item>
                                            <FlexboxGrid.Item as={Col} xs={22} xsHidden smHidden mdHidden={false}>
                                                <div role="grid">
                                                    <FlexboxGrid as={Row} align='bottom'>
                                                        <FlexboxGrid.Item as={Col} xs={22} md={9}>Address</FlexboxGrid.Item>
                                                        <FlexboxGrid.Item as={Col} xs={12} md={5}>{permitType} Number</FlexboxGrid.Item>
                                                        <FlexboxGrid.Item as={Col} xs={6} md={5}>Expiration Date</FlexboxGrid.Item>
                                                        <FlexboxGrid.Item as={Col} xs={6} md={5}>Owner's Name</FlexboxGrid.Item>
                                                    </FlexboxGrid>
                                                </div>
                                            </FlexboxGrid.Item>
                                        </FlexboxGrid>
                                        <div className='tax-license-row-wrapper'>
                                                {
                                                    renewableLicenses?.map((license, index) => (
                                                        (
                                                            <Row key={`license_${index}`} className='tax-license-row'>
                                                                <Col xs={3} md={2}>
                                                                    <BasicStyledCheckbox
                                                                        onChange={(checked) => { toggleLicenseForRenewal(license, checked) }}
                                                                        checked={license?.selected}
                                                                    />
                                                                </Col>
                                                                <Col xs={21} md={22}>
                                                                    <div role="grid">
                                                                        <Row>
                                                                            <Col xs={22} md={9} className='select-properties-address'>{getAddressWithUnitNumber(license?.property_address, license?.unit_number)}</Col>
                                                                            <Col xs={12} md={5}>{license?.license}</Col>
                                                                            <Col xs={12} md={5}>{license?.expiration_date}</Col>
                                                                            <Col xs={24} md={5}>{license?.owner}</Col>
                                                                        </Row>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        )
                                                    ))
                                                }
                                        </div>
                                    </Grid>                                            
                                </div>
                                :
                                <Notice message="No renewable user licenses available." type='info' />
                            }

                            {
                                showNoSelectedLicenseError && (
                                    <Notice message='Please select one or more licenses to continue' className="mb-8" />
                                )
                            }
                        </div>
                        
                    </FlowPageLayout>
                </Content>
            </FlexboxGrid.Item>
        </FlexboxGrid>
    )
}

export default BulkRenewSelectProperties;